import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { isSafari } from '@/shared/util/ssr';
import { usePersistFn } from 'ahooks';
import { message } from 'aui';
import copy from 'copy-to-clipboard';
import React from 'react';

export enum CopyStatus {
  Default = 'default',
  Copied = 'copied',
  Fetching = 'fetching',
}

export function useCopyPage(markdownSpecialFileLink: string) {
  const [copyStatus, setCopyStatus] = React.useState(CopyStatus.Default);

  const timerRef = React.useRef(-1);
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const cacheResponseTextRef = React.useRef<string | undefined>();
  React.useEffect(() => {
    cacheResponseTextRef.current = undefined;
  }, [markdownSpecialFileLink]);

  const copyPage = usePersistFn(async () => {
    try {
      window.clearTimeout(timerRef.current);
      setCopyStatus(CopyStatus.Fetching);

      if (!cacheResponseTextRef.current) {
        const responseText = fetch(markdownSpecialFileLink)
          .then((x) => x.text())
          .then((x) => {
            cacheResponseTextRef.current = x;
            return x;
          });

        if (isSafari) {
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/plain': responseText,
            }),
          ]);
        } else {
          cacheResponseTextRef.current = await responseText;
          const copyResult = copy(cacheResponseTextRef.current);
          if (!copyResult) {
            throw new Error(clientI18n('specialFile.failToCopied'));
          }
        }
      } else {
        const copyResult = copy(cacheResponseTextRef.current);
        if (!copyResult) {
          throw new Error(clientI18n('specialFile.failToCopied'));
        }
      }

      setCopyStatus(CopyStatus.Copied);
      timerRef.current = window.setTimeout(() => {
        setCopyStatus(CopyStatus.Default);
      }, 1000);
      message.success(clientI18n('isCopied'));
    } catch (error: any) {
      message.error(error.message);
      setCopyStatus(CopyStatus.Default);
    }
  });

  return {
    copyStatus,
    copyPage,
  };
}
